/* tslint:disable:jsx-a11y/anchor-is-valid */
import React from "react";
import { inject, observer } from "mobx-react";
import { SiLogo } from "../components/navigation/SiLogo";
import { t } from "../i18n";
import { SiMessage } from "../components/SiMessage";
import { SiFormError } from "../components/SiFormError";
import { SiInput } from "../components/SiInput";
import { SiButton } from "../components/SiButton";
import { If } from "../components/If";
import { LoginPageStore } from "../stores/LoginPageStore";
import { UserStore } from "../stores/UserStore";
import { LoginPageScreen } from "../stores/types";
import { RouteComponentProps, withRouter } from "../utils/withRouter";
import Helmet from "react-helmet";

type ILoginPageProps = RouteComponentProps & {
  loginPageStore?: LoginPageStore;
  userStore?: UserStore;
};

export const LoginPageImpl = inject("loginPageStore")(
  inject("userStore")(observer(class LoginPageImpl extends React.Component<ILoginPageProps> {
    private loginPageStore: LoginPageStore;
    private userStore: UserStore;

    constructor(props: ILoginPageProps) {
      super(props);

      const { loginPageStore, userStore } = this.props;
      if (!loginPageStore) {
        throw new Error("loginPageStore is undefined");
      }

      if (!userStore) {
        throw new Error("userStore is undefined");
      }

      this.loginPageStore = loginPageStore;
      this.userStore = userStore;
    }

    handleUserValueChange = (e) => {
      this.loginPageStore.setState({
        user: {
          ...this.loginPageStore.user,
          [e.target.name]: e.target.value
        }
      })
    }

    render() {
      const activeScreen = this.loginPageStore.activeScreen;

      return (
        <>
          <Helmet>
            <title>Login</title>
          </Helmet>
          <div className="login-wrapper">
            <div className="logo--login">
              <SiLogo type="colored" />
            </div>

            <If
              predicate={
                activeScreen === LoginPageScreen.HomeScreen
              }
            >
              <div className="full-height vertical-align-center content--login tmp-hfix-right">
                <section className="content align-center">
                  <h1 className="mb8">{t("Choose a login method")}</h1>
                  <SiButton
                    onClick={() =>
                    (this.loginPageStore.setState({
                      activeScreen: LoginPageScreen.UseLoginEmail
                    }))
                    }
                    large
                    fullWidth
                  >
                    {t("Get Email Link")}
                  </SiButton>
                  <SiButton
                    onClick={() =>
                    (this.loginPageStore.setState({
                      activeScreen: LoginPageScreen.UseCredentials
                    }))
                    }
                    large
                    fullWidth
                    secondary
                  >
                    {t("Username and Password")}
                  </SiButton>
                </section>
              </div>

            </If>

            <If
              predicate={
                activeScreen === LoginPageScreen.UseCredentials ||
                activeScreen === LoginPageScreen.UseLoginEmail
              }
            >
              <div className="full-height vertical-align-center content--login tmp-hfix-right">
                <If predicate={activeScreen === LoginPageScreen.UseLoginEmail}>
                  <section className="content align-center">
                    <h1 className="mb8">{t("Enter your email address")}</h1>

                    <form
                      onSubmit={event => {
                        event.preventDefault();
                        this.loginPageStore.sendLoginEmail();
                      }}
                    >
                      <If
                        predicate={
                          this.loginPageStore.errorCode === 500 ||
                          this.loginPageStore.errorCode === 0
                        }
                      >
                        <SiMessage type="error">
                          <span>{t("There was a problem logging you in")}</span>
                          <span>
                            {t("Please wait a few minutes and try again")}
                          </span>
                        </SiMessage>
                      </If>

                      <SiFormError
                        errors={
                          this.loginPageStore.errors &&
                          this.loginPageStore.errors.non_field_errors
                        }
                      />

                      <SiInput
                        showLabel={false}
                        className="mb4"
                        label="Email Address"
                        name="email"
                        placeholder="Email Address"
                        required
                        onChange={this.handleUserValueChange}
                        value={this.loginPageStore.user.email}
                        errors={
                          this.loginPageStore.errors &&
                          this.loginPageStore.errors.email
                        }
                      />

                      <If predicate={!this.loginPageStore.sendingEmail}>
                        <SiButton
                          onClick={() => this.loginPageStore.sendLoginEmail()}
                          large
                          fullWidth
                        >
                          {t("Send Login Link")}
                        </SiButton>
                      </If>

                      <If predicate={this.loginPageStore.sendingEmail}>
                        <SiButton large disabled fullWidth>
                          {t("Sending")}...
                        </SiButton>
                      </If>

                      <p className="align-center mt5">
                        <span
                          className="dummy-link"
                          onClick={() =>
                          (this.loginPageStore.setState({
                            activeScreen: LoginPageScreen.UseCredentials
                          }))
                          }
                        >
                          {t("Or login with username and password.")}
                        </span>
                      </p>

                      <input type="submit" className="hidden" />
                    </form>

                    <p className="align-center mt5">
                      <a href="#/privacy-policy">{t("Privacy Policy")}</a>
                    </p>
                  </section>
                </If>

                <If predicate={activeScreen === LoginPageScreen.UseCredentials}>
                  <section className="content align-center">
                    <h1 className="mb8">{t("Sign In")}</h1>

                    <form
                      onSubmit={event => {
                        event.preventDefault();
                        this.loginPageStore.login();
                      }}
                    >
                      <If predicate={this.loginPageStore.errorCode === 500}>
                        <SiMessage type="error">
                          <span>{t("There was a problem logging you in")}</span>
                          <span>
                            {t("Please wait a few minutes and try again")}
                          </span>
                        </SiMessage>
                      </If>

                      <SiFormError
                        errors={
                          this.loginPageStore.errors &&
                          this.loginPageStore.errors.non_field_errors
                        }
                      />

                      <SiInput
                        showLabel={false}
                        className="mb4"
                        label="Username"
                        name="username"
                        placeholder="Username"
                        required
                        onChange={this.handleUserValueChange}
                        value={this.loginPageStore.user.username}
                        errors={
                          this.loginPageStore.errors &&
                          this.loginPageStore.errors.username
                        }
                      />
                      <SiInput
                        showLabel={false}
                        className="mb4"
                        type="password"
                        label="Password"
                        name="password"
                        placeholder="Password"
                        required
                        onChange={this.handleUserValueChange}
                        value={this.loginPageStore.user.password}
                        errors={
                          this.loginPageStore.errors &&
                          this.loginPageStore.errors.password
                        }
                      />

                      <div className="row mb4">
                        <div className="column align-right">
                          <span
                            className="dummy-link"
                            onClick={() => {
                              this.loginPageStore.setState({
                                activeScreen: LoginPageScreen.ResetPassword
                              })
                            }}
                          >
                            {t("Forgot Password?")}
                          </span>
                        </div>
                      </div>

                      <If predicate={!this.loginPageStore.loggingIn}>
                        <SiButton
                          onClick={() => this.loginPageStore.login()}
                          large
                          fullWidth
                        >
                          {t("Sign In")}
                        </SiButton>
                      </If>

                      <If predicate={this.loginPageStore.loggingIn}>
                        <SiButton large disabled fullWidth>
                          {t("Signing In")}...
                        </SiButton>
                      </If>

                      <input type="submit" className="hidden" />
                    </form>
                    <p className="align-center mt5">
                      <span
                        className="dummy-link"
                        onClick={() =>
                        (this.loginPageStore.setState({
                          activeScreen: LoginPageScreen.UseLoginEmail
                        }))
                        }
                      >
                        {t("Or login with an email link.")}
                      </span>
                    </p>


                    <p className="align-center mt5">
                      <a href="#/privacy-policy">{t("Privacy Policy")}</a>
                    </p>
                  </section>
                </If>
              </div>
            </If>

            <If predicate={activeScreen === LoginPageScreen.LoginEmailSent}>
              <div className="full-height vertical-align-center content--login">
                <section className="content align-center">
                  <h1 className="mb8">{t("Please check your inbox")}</h1>

                  <p>
                    {t(
                      "Please check your inbox and click the link we just sent you to login."
                    )}
                  </p>
                  <p>
                    {t(
                      "If you dont receive an email in the next few minutes, please check your spam folder in case it was sent there."
                    )}
                  </p>
                </section>
              </div>
            </If>

            <If
              predicate={
                activeScreen === LoginPageScreen.ResetPassword ||
                activeScreen === LoginPageScreen.ResetPasswordEmailSent
              }
            >
              <div className="full-height vertical-align-center content--login">
                <If predicate={activeScreen === LoginPageScreen.ResetPassword}>
                  <section className="content align-center">
                    <h1 className="mb8">{t("Enter your email address")}</h1>

                    <form
                      onSubmit={event => {
                        event.preventDefault();
                        this.loginPageStore.sendResetPasswordEmail();
                      }}
                    >
                      <SiFormError
                        errors={this.loginPageStore.errors.non_field_errors}
                      />

                      <SiInput
                        showLabel={false}
                        className="mb4"
                        label="Email Address"
                        name="email"
                        placeholder="Email Address"
                        required
                        onChange={this.handleUserValueChange}
                        value={this.loginPageStore.user.email}
                        errors={this.loginPageStore.errors.email}
                      />

                      <If predicate={!this.loginPageStore.sendingEmail}>
                        <SiButton
                          onClick={() =>
                            this.loginPageStore.sendResetPasswordEmail()
                          }
                          large
                          fullWidth
                        >
                          {t("Request Password Reset")}
                        </SiButton>
                      </If>

                      <If predicate={this.loginPageStore.sendingEmail}>
                        <SiButton large disabled fullWidth>
                          {t("Requesting")}...
                        </SiButton>
                      </If>

                      <input type="submit" className="hidden" />
                    </form>
                  </section>
                </If>

                <If
                  predicate={
                    activeScreen === LoginPageScreen.ResetPasswordEmailSent
                  }
                >
                  <section className="content align-center">
                    <h1 className="mb8">{t("Password Reset Email Sent")}</h1>

                    <p style={{ textAlign: "center" }}>
                      {t(
                        "You should be receiving an email shortly with instructions on resetting your password."
                      )}
                    </p>
                  </section>
                </If>
              </div>
            </If>
          </div>
        </>
      );
    }
  }))
);

export const LoginPage = withRouter(LoginPageImpl);
